import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Upload, Check, ArrowRight, ArrowLeft, Building2, CreditCard, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { getCsrfToken } from '../lib/api';

const API = ''; // use relative URLs for production-safe API calls

export default function SetupWizard() {
  const navigate = useNavigate();
  const { user, checkSession } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const [companyData, setCompanyData] = useState({
    company_name: '',
    company_owner: '',
    company_street: '',
    company_city: '',
    company_phone: '',
    company_email: '',
    company_vat_id: '',
    default_bank_name: '',
    default_iban: '',
    default_bic: '',
    default_payment_terms: '14 Tage netto nach Rechnungserhalt',
  });

  // Pre-fill on mount
  useEffect(() => {
    if (!user) return;
    fetch(`${API}/api/admin/documents/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setCompanyData(prev => ({
            ...prev,
            company_name: d.company_name || `${user.first_name} ${user.last_name}`,
            company_owner: d.company_owner || `${user.first_name} ${user.last_name}`,
            company_email: d.company_email || user.email,
            company_phone: d.company_phone || '',
            company_street: d.company_street || '',
            company_city: d.company_city || '',
            company_vat_id: d.company_vat_id || '',
            default_bank_name: d.default_bank_name || '',
            default_iban: d.default_iban || '',
            default_bic: d.default_bic || '',
            default_payment_terms: d.default_payment_terms || '14 Tage netto nach Rechnungserhalt',
          }));
          if (d.logo_url) setLogoPreview(d.logo_url.startsWith('http') ? d.logo_url : `${API}${d.logo_url}`);
        }
      });
  }, [user]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo darf maximal 5 MB groß sein');
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile) return true;
    const fd = new FormData();
    fd.append('file', logoFile);
    const csrf = getCsrfToken() || '';
    const res = await fetch(`${API}/api/admin/documents/upload-logo`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-Token': csrf },
      body: fd,
    });
    return res.ok;
  };

  const saveCompanyData = async () => {
    const csrf = getCsrfToken() || '';
    const res = await fetch(`${API}/api/admin/documents/settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
      body: JSON.stringify(companyData),
    });
    return res.ok;
  };

  const finishSetup = async () => {
    setLoading(true);
    try {
      if (logoFile) {
        const ok = await uploadLogo();
        if (!ok) {
          toast.error('Logo-Upload fehlgeschlagen');
          setLoading(false);
          return;
        }
      }
      const ok = await saveCompanyData();
      if (!ok) {
        toast.error('Daten speichern fehlgeschlagen');
        setLoading(false);
        return;
      }
      const csrf = getCsrfToken() || '';
      const completeRes = await fetch(`${API}/api/admin/setup-complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': csrf },
      });
      if (!completeRes.ok) {
        toast.error('Setup-Abschluss fehlgeschlagen');
        setLoading(false);
        return;
      }
      await checkSession();
      toast.success('Einrichtung abgeschlossen! Willkommen in Ihrem Portal.');
      navigate('/admin');
    } catch (e) {
      toast.error('Fehler: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const skipSetup = async () => {
    if (!window.confirm('Setup wirklich überspringen? Sie können die Einstellungen später jederzeit nachholen.')) return;
    setLoading(true);
    try {
      const csrf = getCsrfToken() || '';
      const res = await fetch(`${API}/api/admin/setup-complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': csrf },
      });
      if (!res.ok) {
        toast.error('Konnte Setup nicht abschließen. Bitte erneut anmelden und versuchen.');
        setLoading(false);
        return;
      }
      await checkSession();
      navigate('/admin');
    } catch (e) {
      toast.error('Fehler: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#9B1B30]/5 via-white to-[#2E2A88]/5 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#9B1B30]/10 text-[#9B1B30] px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Willkommen, {user?.first_name}!
          </div>
          <h1 className="text-3xl md:text-4xl font-barlow font-bold text-[#1A1A1A] mb-3" data-testid="setup-wizard-title">
            Lass uns Ihr Portal einrichten
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            In wenigen Schritten konfigurieren wir Ihr eigenes, individuelles System – für Rechnungen, Angebote und mehr.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Schritt {step} von {totalSteps}</span>
            <span>{Math.round(progress)}% fertig</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <Card className="shadow-xl border-0">
          <CardContent className="p-6 md:p-10">
            {/* STEP 1: Logo */}
            {step === 1 && (
              <div className="space-y-6" data-testid="setup-step-logo">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-xl bg-[#9B1B30]/10 text-[#9B1B30] flex items-center justify-center">
                    <Upload className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#1A1A1A]">Ihr Firmenlogo</h2>
                    <p className="text-sm text-gray-500">Wird auf Rechnungen, Angeboten & im Portal-Header angezeigt</p>
                  </div>
                </div>

                <label className="block">
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} data-testid="setup-logo-input" />
                  <div className="border-2 border-dashed border-gray-300 hover:border-[#9B1B30] rounded-xl p-8 text-center cursor-pointer transition-colors bg-gray-50/50">
                    {logoPreview ? (
                      <div className="space-y-3">
                        <img src={logoPreview} alt="Logo" className="max-h-32 mx-auto object-contain" />
                        <p className="text-sm text-[#9B1B30] font-medium">Klicken um Logo zu ändern</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-12 h-12 mx-auto text-gray-400" />
                        <p className="font-medium text-gray-700">Logo hochladen</p>
                        <p className="text-xs text-gray-500">PNG, JPG, SVG · max. 5 MB · empfohlen 800×256 px</p>
                      </div>
                    )}
                  </div>
                </label>

                <p className="text-xs text-gray-500">
                  Tipp: Sie können diesen Schritt überspringen und später ein Logo hochladen.
                </p>
              </div>
            )}

            {/* STEP 2: Firmendaten */}
            {step === 2 && (
              <div className="space-y-5" data-testid="setup-step-company">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-xl bg-[#2E2A88]/10 text-[#2E2A88] flex items-center justify-center">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#1A1A1A]">Firmendaten</h2>
                    <p className="text-sm text-gray-500">Erscheint im Briefkopf Ihrer Dokumente</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Firmenname *</Label>
                    <Input value={companyData.company_name} onChange={(e) => setCompanyData({...companyData, company_name: e.target.value})} placeholder="Mustermann GmbH" data-testid="setup-company-name" />
                  </div>
                  <div>
                    <Label>Inhaber/Geschäftsführer</Label>
                    <Input value={companyData.company_owner} onChange={(e) => setCompanyData({...companyData, company_owner: e.target.value})} placeholder="Max Mustermann" />
                  </div>
                  <div>
                    <Label>Straße & Hausnummer</Label>
                    <Input value={companyData.company_street} onChange={(e) => setCompanyData({...companyData, company_street: e.target.value})} placeholder="Beispielstr. 12" />
                  </div>
                  <div>
                    <Label>PLZ & Ort</Label>
                    <Input value={companyData.company_city} onChange={(e) => setCompanyData({...companyData, company_city: e.target.value})} placeholder="12345 Beispielort" />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input value={companyData.company_phone} onChange={(e) => setCompanyData({...companyData, company_phone: e.target.value})} placeholder="0123 456789" />
                  </div>
                  <div>
                    <Label>E-Mail</Label>
                    <Input type="email" value={companyData.company_email} onChange={(e) => setCompanyData({...companyData, company_email: e.target.value})} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>USt-ID / Steuernummer (optional)</Label>
                    <Input value={companyData.company_vat_id} onChange={(e) => setCompanyData({...companyData, company_vat_id: e.target.value})} placeholder="DE123456789" />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Bank + Zahlungsbedingungen */}
            {step === 3 && (
              <div className="space-y-5" data-testid="setup-step-bank">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#1A1A1A]">Bankverbindung & Zahlung</h2>
                    <p className="text-sm text-gray-500">Wird unter Rechnungen angezeigt – können Sie später ändern</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Bankname</Label>
                    <Input value={companyData.default_bank_name} onChange={(e) => setCompanyData({...companyData, default_bank_name: e.target.value})} placeholder="Sparkasse Musterstadt" />
                  </div>
                  <div>
                    <Label>BIC</Label>
                    <Input value={companyData.default_bic} onChange={(e) => setCompanyData({...companyData, default_bic: e.target.value})} placeholder="MUSTDE12XXX" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>IBAN</Label>
                    <Input value={companyData.default_iban} onChange={(e) => setCompanyData({...companyData, default_iban: e.target.value})} placeholder="DE12 3456 7890 1234 5678 90" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Standard-Zahlungsbedingungen</Label>
                    <Textarea rows={2} value={companyData.default_payment_terms} onChange={(e) => setCompanyData({...companyData, default_payment_terms: e.target.value})} />
                  </div>
                </div>

                <div className="bg-[#9B1B30]/5 border-l-4 border-[#9B1B30] rounded-r-lg p-4 mt-6">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-[#1A1A1A] text-sm mb-1">Fast geschafft!</p>
                      <p className="text-sm text-gray-600">Klicken Sie auf "Fertigstellen", um Ihr Portal zu aktivieren. Sie können alle Einstellungen später jederzeit unter "Einstellungen" anpassen.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t">
              <div>
                {step > 1 && (
                  <Button variant="outline" onClick={() => setStep(step - 1)} disabled={loading} data-testid="setup-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={skipSetup} className="text-sm text-gray-500 hover:text-gray-700 px-3" disabled={loading} data-testid="setup-skip-btn">
                  Überspringen
                </button>
                {step < totalSteps ? (
                  <Button onClick={() => setStep(step + 1)} className="bg-[#9B1B30] hover:bg-[#9B1B30]/90 text-white" disabled={loading} data-testid="setup-next-btn">
                    Weiter <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={finishSetup} className="bg-[#9B1B30] hover:bg-[#9B1B30]/90 text-white" disabled={loading} data-testid="setup-finish-btn">
                    {loading ? 'Speichern…' : 'Fertigstellen'} <Check className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
